import { useMemo } from 'react';
import {
  Scan,
  ScanSchema,
} from '../../../../../services/entities/impl/blfApi/schemas/scan';
import {
  ValidationCounts,
  ValidationSummary,
  createValidationSummary,
} from '../../../utils/validation/createValidationSummary';
import {
  ValidationMessage,
  ValidationResult,
  validateSchema,
} from '../../../utils/validation/validateSchema';

/**
 * Hook for validating scan data
 *
 * @param scans The array of scans to validate
 * @returns Object containing individual validation results and a summary
 */
export const useScansValidation = (
  scans: Scan[] | undefined
): {
  individualResults: Record<string, ValidationResult>;
  summary: ValidationSummary;
} => {
  return useMemo(() => {
    const individualResults: Record<string, ValidationResult> = {};
    const emptyCounts: ValidationCounts = { errors: 0, warnings: 0, total: 0 };

    // Skip validation if no data
    if (!scans || scans.length === 0) {
      return {
        individualResults,
        summary: {
          result: { valid: true, messages: [] },
          counts: emptyCounts,
        },
      };
    }

    // Track overall validation counts
    let totalErrors = 0;
    let totalWarnings = 0;

    // Validate each scan
    scans.forEach((scan) => {
      // Validate against schema
      const result = validateSchema(scan, ScanSchema);

      // Add warnings for empty arrays for new fields while we transition to new schema.  Things like cameraIDs, collectionIDs, flashVersions, and appVersions are migrating to arrays in new fields instead of single values.
      const additionalWarnings: ValidationMessage[] = [];

      // Check cameraIDs
      if (
        scan.cameraIDs &&
        Array.isArray(scan.cameraIDs) &&
        scan.cameraIDs.length === 0
      ) {
        additionalWarnings.push({
          path: 'cameraIDs',
          message: 'Camera IDs array is empty',
          severity: 'warning',
        });
      }

      // Check collectionIDs
      if (
        scan.collectionIDs &&
        Array.isArray(scan.collectionIDs) &&
        scan.collectionIDs.length === 0
      ) {
        additionalWarnings.push({
          path: 'collectionIDs',
          message: 'Collection IDs array is empty',
          severity: 'warning',
        });
      }

      // Check flashVersions
      if (
        scan.flashVersions &&
        Array.isArray(scan.flashVersions) &&
        scan.flashVersions.length === 0
      ) {
        additionalWarnings.push({
          path: 'flashVersions',
          message: 'Flash versions array is empty',
          severity: 'warning',
        });
      }

      // Check appVersions
      if (
        scan.appVersions &&
        Array.isArray(scan.appVersions) &&
        scan.appVersions.length === 0
      ) {
        additionalWarnings.push({
          path: 'appVersions',
          message: 'App versions array is empty',
          severity: 'warning',
        });
      }

      // If we have warnings, add them to the result
      if (additionalWarnings.length > 0) {
        result.messages = [...result.messages, ...additionalWarnings];
        result.valid = false; // Set to false so warnings will be displayed
        totalWarnings += additionalWarnings.length;
      }

      // Store result keyed by ID
      if (scan.id) {
        individualResults[scan.id] = result;
      }

      // Add schema validation totals
      if (!result.valid) {
        result.messages.forEach((msg) => {
          if (msg.severity === 'error') totalErrors++;
          if (msg.severity === 'warning' && !additionalWarnings.includes(msg))
            totalWarnings++;
        });
      }
    });

    // Create counts
    const counts: ValidationCounts = {
      errors: totalErrors,
      warnings: totalWarnings,
      total: totalErrors + totalWarnings,
    };

    // Create summary
    const summaryResult = createValidationSummary(counts);

    return {
      individualResults,
      summary: {
        result: summaryResult,
        counts,
      },
    };
  }, [scans]);
};
